import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useGetSegments } from '@/api/segments/useGetSegments';
import { Button } from '@/component-library/primitives/Button/Button';
import Chip from '@/component-library/primitives/Chip/Chip';
import ChevronIcon from '@/component-library/primitives/Icons/ChevronIcon/ChevronIcon';
import Link from '@/component-library/primitives/Link/Link';
import OptionPicker from '@/component-library/primitives/OptionPicker/OptionPicker';
import Stepper from '@/component-library/primitives/Stepper/Stepper';
import { Textarea } from '@/component-library/primitives/Textarea/Textarea';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import Diagram from '@/component-library/widgets/Diagram/Diagram';
import { Input } from '@/component-library/widgets/Input/Input';
import SelectField from '@/component-library/widgets/SelectField/SelectField';
import { CampaignDetails } from '@/models/Campaigns';
import CreateCampaignLastStep from '@/modules/Dashboard/Campaigns/CreateCampaign/CreateCampaignLastStep';
import { AppRoutes } from '@/utils/routes/router';

import 'reactflow/dist/style.css';

const CreateCampaign = () => {
  const { data: segmentData } = useGetSegments();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const [activeStep, setActiveStep] = useState(0);
  const [showConfigureTrigger, setShowConfigureTrigger] = useState(false);
  const [allSubscribedPeopleIsActive, setAllSubscribedPeopleIsActive] = useState(true);
  const [allSubscribedAndUnsubscribedPeopleIsActive, setAllSubscribedAndUnsubscribedPeopleIsActive] = useState(false);
  const [goalIsActive, setGoalIsActive] = useState(true);
  const [noGoalIsActive, setNoGoalIsActive] = useState(false);
  const [entersSegmentIsActive, setEntersSegmentIsActive] = useState(true);
  const [leavesSegmentIsActive, setLeavesSegmentIsActive] = useState(false);
  const [achieveGoalIsAactive, setAchieveTheGoalIsActive] = useState(true);
  const [stopMatchingTriggerIsActive, setStopMatchingTriggerIsActive] = useState(false);
  const [achieveGoalOrStopMatchingTriggerIsActive, setAchieveGoalOrStopMatchingTriggerIsActive] = useState(false);
  const [peopleDontExitEarlyIsActive, setPeopleDontExitEarlyIsActive] = useState(false);

  const timeOptions = [
    { text: t('CreateCampaignPage.daysTimeOption') },
    { text: t('CreateCampaignPage.weeksTimeOption') },
    { text: t('CreateCampaignPage.monthsTimeOption') },
    { text: t('CreateCampaignPage.yearsTimeOption') },
  ];
  const [selectedTimeOption, setSelectedTimeOption] = useState(timeOptions[0]);

  const actionOptions = [
    { text: t('CreateCampaignPage.openingActionOption') },
    { text: t('CreateCampaignPage.receivingActionOption') },
    { text: t('CreateCampaignPage.trackedLinkActionOption') },
  ];
  const [selectedActionOption, setSelectedActionOption] = useState(actionOptions[0]);

  const steps = [
    t('CreateCampaignPage.infoStep'),
    t('CreateCampaignPage.triggersStep'),
    t('CreateCampaignPage.settingsStep'),
    t('CreateCampaignPage.goalAndExitStep'),
    t('CreateCampaignPage.workflowStep'),
    t('CreateCampaignPage.reviewStep'),
  ];

  const triggers = [
    {
      triggerTitle: 'Event',
      triggerShortDescription: t('CreateCampaignPage.whenSomeonePerformsOnEventDescription'),
      triggerLongDescriptionTitle: t('CreateCampaignPage.eventTriggeredCampaignsLabel'),
      triggerLongDescriptionText: t('CreateCampaignPage.eventTriggeredCampaignsDescription'),
    },
    {
      triggerTitle: 'Important date',
      triggerShortDescription: 'Automatically send scheduled messages on patient birthdays or care anniversaries',
      triggerLongDescriptionTitle: 'Date-based Triggered Campaigns',
      triggerLongDescriptionText:
        "Send timely messages on important dates in your patients' lives. Perfect for wellness reminders, annual checkup prompts, and building patient relationships with personalized messaging.",
    },
    {
      triggerTitle: 'Object updated',
      triggerShortDescription: 'Trigger communications when new lab results are available for patients',
      triggerLongDescriptionTitle: 'Lab Result Notification Campaigns',
      triggerLongDescriptionText:
        'Keep your patients informed about their health with timely notifications about lab results. Automate the follow-up process while maintaining a personal touch and encouraging appropriate actions based on results.',
    },
    {
      triggerTitle: 'Attribute or Segment',
      triggerShortDescription: 'Send messages when appointments are scheduled, confirmed, or missed',
      triggerLongDescriptionTitle: 'Appointment-based Campaigns',
      triggerLongDescriptionText:
        'Reduce no-shows and improve patient experience by automatically sending reminders, preparation instructions, or follow-up messages based on appointment status changes in your scheduling system.',
    },
    {
      triggerTitle: 'Segment change',
      triggerShortDescription: "Notify patients when it's time to refill their prescriptions",
      triggerLongDescriptionTitle: 'Medication Management Campaigns',
      triggerLongDescriptionText:
        'Improve medication adherence by proactively reminding patients when refills are due. Help patients maintain their treatment plans with timely, automated communications at exactly the right moment.',
    },
    {
      triggerTitle: 'Form submission',
      triggerShortDescription: 'Trigger messages when patients complete and submit online forms',
      triggerLongDescriptionTitle: 'Form Response Campaigns',
      triggerLongDescriptionText:
        'Acknowledge form submissions and provide next steps to patients who complete intake forms, surveys, or other digital paperwork. Streamline your workflow by automating responses based on form submissions.',
    },
    {
      triggerTitle: 'Relationship added or changed',
      triggerShortDescription: 'Send communications when patient relationships are updated in your system',
      triggerLongDescriptionTitle: 'Relationship Management Campaigns',
      triggerLongDescriptionText:
        'Keep your records current and patients informed when relationships change. Perfect for updating emergency contacts, physician referrals, or care team modifications with timely, automated communications.',
    },
    {
      triggerTitle: 'Webhook',
      triggerShortDescription: 'Connect with external systems to trigger patient communications',
      triggerLongDescriptionTitle: 'Integration-based Campaigns',
      triggerLongDescriptionText:
        'Leverage data from your entire healthcare tech stack to trigger timely patient communications. Seamlessly connect with EHRs, billing systems, and other healthcare platforms to automate messaging at critical touchpoints.',
    },
  ];

  const [selectedTrigger, setSelectedTrigger] = useState(triggers[0]);

  const availableSegments = useMemo(
    () =>
      segmentData?.map((segment) => {
        return {
          label: segment.name,
          value: segment.id,
        };
      }),
    [segmentData],
  );

  const messageNodes = [
    t('CreateCampaignPage.emailNodeLabel'),
    t('CreateCampaignPage.pushNotificationNodeLabel'),
    t('CreateCampaignPage.smsNodeLabel'),
  ];

  const dataNodes = [
    t('CreateCampaignPage.sendEventNodeLabel'),
    t('CreateCampaignPage.batchUpdateNodeLabel'),
    t('CreateCampaignPage.manualSegmentUpdateNodeLabel'),
    t('CreateCampaignPage.createOrUpdatePersonNodeLabel'),
    t('CreateCampaignPage.queryCollectionNodeLabel'),
  ];

  const delaysNodes = [
    t('CreateCampaignPage.waitUntilNodeLabel'),
    t('CreateCampaignPage.timeDelayNodeLabel'),
    t('CreateCampaignPage.timeWindowNodeLabel'),
  ];

  const flowControlNodes = [
    t('CreateCampaignPage.trueFalseBranchNodeLabel'),
    t('CreateCampaignPage.multiSplitBranchNodeLabel'),
    t('CreateCampaignPage.exitNodeLabel'),
  ];

  const onDragStart = (event: any, nodeName: string, color: string, emoji: string) => {
    event.dataTransfer.setData('reactflow-nodeName', nodeName);
    event.dataTransfer.setData('reactflow-color', color);
    event.dataTransfer.setData('reactflow-emoji', emoji);
    event.dataTransfer.effectAllowed = 'move';
  };

  useEffect(() => {
    let timerBeforeNavigation: NodeJS.Timeout;
    if (toastInfo.toastType === 'success') {
      timerBeforeNavigation = setTimeout(() => navigate(AppRoutes.campaigns), 2000);
    }
    return () => {
      clearTimeout(timerBeforeNavigation);
    };
  }, [navigate, toastInfo]);

  const schema = useMemo(() => {
    return [
      yup.object({
        name: yup
          .string()
          .trim()
          .required(t('CommonErrors.nameRequired'))
          .min(1, t('CreateCampaignPage.nameTooShort', { minLength: 1 }))
          .max(50, t('CreateCampaignPage.nameTooLong', { maxLength: 50 })),
        description: yup
          .string()
          .trim()
          .required(t('CommonErrors.descriptionRequired'))
          .min(1, t('CreateCampaignPage.descriptionTooShort', { minLength: 1 }))
          .max(250, t('CreateCampaignPage.descriptionTooLong', { maxLength: 250 })),
      }),
      yup.object({
        eventName:
          selectedTrigger.triggerTitle === t('CreateCampaignPage.whenSomeonePerformsOnEventTriggerTitle')
            ? yup.string().trim().required(t('CreateCampaignPage.eventNameRequired'))
            : yup.string(),
        shouldReceiveSegments: yup
          .array()
          .required(t('CreateCampaignPage.shouldReceiveSegmentsRequired'))
          .nullable()
          .min(1, t('CreateCampaignPage.shouldReceiveSegmentsRequired')),
      }),
      yup.object({}),
      yup.object({
        conversionDuration: goalIsActive
          ? yup
              .number()
              .min(1, t('CreateCampaignPage.minDurationError'))
              .max(99, t('CreateCampaignPage.maxDurationError'))
              .transform((value) => (isNaN(value) ? undefined : value))
              .required(t('CreateCampaignPage.durationRequired'))
          : yup.string(),
      }),
      yup.object({}),
      yup.object({}),
    ];
  }, [t, selectedTrigger, goalIsActive]);

  const currentValidationSchema = useMemo(() => schema[activeStep], [schema, activeStep]);

  const methods = useForm<CampaignDetails>({
    shouldUnregister: false,
    resolver: yupResolver(currentValidationSchema),
    mode: 'onChange',
  });

  const {
    control,
    trigger,
    register,
    formState: { errors },
  } = methods;

  const exitCondition = () => {
    if (achieveGoalIsAactive) {
      return t('CreateCampaignPage.theyAchieveTheGoalOption');
    } else if (stopMatchingTriggerIsActive) {
      return t('CreateCampaignPage.theyStopMatchingTriggerOption');
    } else if (achieveGoalOrStopMatchingTriggerIsActive) {
      return t('CreateCampaignPage.theyAchieveTheGoalOrStopMatchingTrigger');
    } else if (peopleDontExitEarlyIsActive) {
      return t('CreateCampaignPage.peopleDontExitEarly');
    }
  };

  const onBack = () => {
    activeStep === 0 ? navigate(AppRoutes.campaigns) : setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <>
      <div className="flex flex-col items-center mt-12">
        <div>
          <div className="flex justify-center">
            <Stepper steps={steps} activeStep={activeStep} />
          </div>
          <FormProvider {...methods}>
            <form className="min-w-[50rem] mx-auto">
              {/* Info step */}
              {activeStep === 0 && (
                <div className="space-y-6">
                  <section className="space-y-4 rounded-lg text-custom-aliceBlue">
                    <Input
                      className="text-custom-aliceBlue border-gray-700 placeholder-custom-aliceBlue placeholder:font-semibold"
                      placeholder={t('CreateCampaignPage.namePlaceholder')}
                      {...register('name')}
                      error={errors.name?.message as string}
                    />
                  </section>
                  <section className="pr-6 pl-6 pb-8 pt-6 space-y-4 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
                    <h2 className="text-base font-semibold text-custom-aliceBlue flex items-center mb-8">
                      {t('CreateCampaignPage.descriptionLabel')}
                    </h2>
                    <Textarea
                      {...register('description')}
                      placeholder={t('CreateCampaignPage.descriptionPlaceholder')}
                      error={errors.description?.message}
                      className="overflow-hidden placeholder:font-normal"
                    />
                  </section>
                  <div className="flex justify-end">
                    <Button variant="cancel" onClick={() => navigate(AppRoutes.campaigns)}>
                      {t('CreateCampaignPage.linkCancel')}
                    </Button>
                    <Button type="button" className="ml-8" onClick={onNext}>
                      {t('CreateCampaignPage.buttonNext')}
                    </Button>
                  </div>
                </div>
              )}
              {/* Triggers step */}
              {activeStep === 1 && !showConfigureTrigger && (
                <div className="lg:w-[70rem] 3xl:w-[90rem] ">
                  <h1 className="text-custom-aliceBlue font-semibold text-xl mb-8">
                    {t('CreateCampaignPage.whatTriggersCampaignLabel')}
                  </h1>
                  <div className="flex flex-row lg:gap-x-28 3xl:gap-x-40">
                    <div className="flex flex-col flex-wrap gap-y-4">
                      {triggers.map((trigger: any, index: number) => (
                        <React.Fragment key={index}>
                          <section
                            className={`lg:w-[30rem] 3xl:w-[35rem] min-h-[6.43rem] px-3 pt-3 pb-4 shadow rounded-lg bg-custom-gray text-gray-200 cursor-pointer border border-transparent
                hover:!border-primary-400 hover:bg-settingsCard ${
                  selectedTrigger?.triggerTitle === trigger.triggerTitle
                    ? ' !border-primary-400 shadow-[0px_0px_13px_0px_#82E6B8]'
                    : ''
                }`}
                            onClick={() => {
                              setSelectedTrigger(trigger);
                            }}
                          >
                            <div className="flex flex-row items-start gap-4">
                              <div className="min-w-[4rem] w-16 h-16"></div>
                              <div className="flex flex-col">
                                <h2 className="text-base flex items-center mb-4 font-semibold text-custom-aliceBlue">
                                  {trigger.triggerTitle}
                                </h2>
                                <p className="leading-5">{trigger.triggerShortDescription}</p>
                              </div>
                            </div>
                          </section>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex flex-col flex-wrap gap-10">
                      <div className="space-y-6 lg:w-[35rem] 3xl:w-[41rem]">
                        <div className="flex flex-col">
                          <h1 className="font-semibold text-xl mb-8 text-custom-aliceBlue">
                            {selectedTrigger.triggerLongDescriptionTitle}
                          </h1>
                          <p className="leading-5 text-gray-200">{selectedTrigger.triggerLongDescriptionText}</p>
                          <p className="text-custom-aliceBlue font-semibold mt-6 mb-10">
                            {t('CreateCampaignPage.bestForThingsLikeLabel')}:
                          </p>
                          <div className="flex flex-row justify-start gap-14">
                            {/* Appointment Reminder Card */}
                            <div className="w-[10.563rem] h-[8.75rem] border rounded-2xl border-gray-700 bg-gray-800/40 p-4 flex flex-col justify-between hover:border-emerald-500/50 transition-all duration-300">
                              <div className="flex items-center">
                                <div className="bg-emerald-500/20 p-2 rounded-lg mr-2">
                                  <svg
                                    className="w-4 h-4 text-emerald-400"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V8.5C3 7.39543 3.89543 6.5 5 6.5H19C20.1046 6.5 21 7.39543 21 8.5Z"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                                <span className="text-xs font-medium text-custom-aliceBlue">Appointment Reminders</span>
                              </div>
                              <div className="mt-2">
                                <div className="flex items-center mb-1">
                                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5"></div>
                                  <span className="text-xs text-gray-300">24-hour reminders</span>
                                </div>
                                <div className="flex items-center">
                                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5"></div>
                                  <span className="text-xs text-gray-300">Reduce no-shows</span>
                                </div>
                              </div>
                            </div>

                            {/* Medication Adherence Card */}
                            <div className="w-[10.563rem] h-[8.75rem] border rounded-2xl border-gray-700 bg-gray-800/40 p-4 flex flex-col justify-between hover:border-emerald-500/50 transition-all duration-300">
                              <div className="flex items-center">
                                <div className="bg-emerald-500/20 p-2 rounded-lg mr-2">
                                  <svg
                                    className="w-4 h-4 text-emerald-400"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M16 8H8M16 8C18.2091 8 20 9.79086 20 12V18C20 20.2091 18.2091 22 16 22H8C5.79086 22 4 20.2091 4 18V12C4 9.79086 5.79086 8 8 8M16 8V6C16 3.79086 14.2091 2 12 2C9.79086 2 8 3.79086 8 6V8"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                                <span className="text-xs font-medium text-custom-aliceBlue">Medication Adherence</span>
                              </div>
                              <div className="mt-2">
                                <div className="flex items-center mb-1">
                                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5"></div>
                                  <span className="text-xs text-gray-300">Daily reminders</span>
                                </div>
                                <div className="flex items-center">
                                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5"></div>
                                  <span className="text-xs text-gray-300">Refill notifications</span>
                                </div>
                              </div>
                            </div>

                            {/* Health Education Card */}
                            <div className="w-[10.563rem] h-[8.75rem] border rounded-2xl border-gray-700 bg-gray-800/40 p-4 flex flex-col justify-between hover:border-emerald-500/50 transition-all duration-300">
                              <div className="flex items-center">
                                <div className="bg-emerald-500/20 p-2 rounded-lg mr-2">
                                  <svg
                                    className="w-4 h-4 text-emerald-400"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M19.5 14.25V11.625C19.5 9.76104 17.989 8.25 16.125 8.25H14.625C14.0037 8.25 13.5 7.74632 13.5 7.125V5.625C13.5 3.76104 11.989 2.25 10.125 2.25H8.25M9.75 21.75H10.125C12.4902 21.75 13.673 21.75 14.5884 21.3169C15.3931 20.9307 16.0307 20.2931 16.4169 19.4884C16.85 18.573 16.85 17.3901 16.85 15.025V9.775"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                                <span className="text-xs font-medium text-custom-aliceBlue">Health Education</span>
                              </div>
                              <div className="mt-2">
                                <div className="flex items-center mb-1">
                                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5"></div>
                                  <span className="text-xs text-gray-300">Condition info</span>
                                </div>
                                <div className="flex items-center">
                                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5"></div>
                                  <span className="text-xs text-gray-300">Wellness resources</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button type="button" variant="transparent">
                          {t('CreateCampaignPage.linkLearnMore')}
                        </Button>
                        <Button type="button" className="ml-8" onClick={() => setShowConfigureTrigger(true)}>
                          {t('CreateCampaignPage.configureTrigger')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeStep === 1 && showConfigureTrigger && (
                <div className="space-y-6">
                  <div>
                    <h1 className="text-custom-aliceBlue font-semibold text-xl mb-6">
                      {t('CreateCampaignPage.whatTriggersCampaignLabel')}
                    </h1>
                  </div>
                  <div>
                    <section className="p-6 space-y-4 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
                      <h2 className="text-base font-semibold flex items-center mb-4">{selectedTrigger.triggerTitle}</h2>
                      <p className="text-gray-200">{selectedTrigger.triggerShortDescription}</p>
                      {selectedTrigger.triggerTitle ===
                        t('CreateCampaignPage.whenSomeonePerformsOnEventTriggerTitle') && (
                        <Input
                          placeholder={t('CreateCampaignPage.eventNamePlaceholder')}
                          {...register('eventName')}
                          error={errors.eventName?.message as string}
                          className="border-gray-700"
                        />
                      )}
                    </section>
                    <div className="flex justify-end">
                      <Button variant="transparent" onClick={() => setShowConfigureTrigger(false)}>
                        {t('CreateCampaignPage.linkEditType')}
                      </Button>
                    </div>
                    <section className="px-6 pt-6 pb-8 space-y-4 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
                      <h2 className="text-base font-semibold flex items-center mb-4">
                        {t('CreateCampaignPage.segmentsLabel')}
                      </h2>
                      <SelectField
                        name="shouldReceiveSegments"
                        label={
                          <Trans
                            i18nKey="CreateCampaignPage.segmentsIncludedLabel"
                            components={{
                              primary: (
                                <span
                                  className={`${
                                    errors.shouldReceiveSegments?.message ? 'text-errorRed-500' : 'text-yellow-200'
                                  }`}
                                />
                              ),
                            }}
                          />
                        }
                        control={control}
                        options={availableSegments}
                        errorMsg={errors.shouldReceiveSegments?.message}
                        className={'mb-2'}
                        isMulti={true}
                      />
                      <div className="flex justify-end mt-4">
                        <Link to={AppRoutes.segments} label={`⚙️ ${t('CreateCampaignPage.linkManageConditions')}`} />
                      </div>
                    </section>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="cancel" onClick={() => navigate(AppRoutes.campaigns)}>
                      {t('CreateCampaignPage.linkCancel')}
                    </Button>
                    <Button type="button" className="ml-8" onClick={onNext}>
                      {t('CreateCampaignPage.buttonNext')}
                    </Button>
                  </div>
                </div>
              )}
              {/* Settings step */}
              {activeStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h1 className="text-custom-aliceBlue font-semibold text-xl mb-4">
                      {t('CreateCampaignPage.messageSettingsLabel')}
                    </h1>
                    <p className="text-custom-aliceBlue mt-4 w-[85%]">
                      {t('CreateCampaignPage.chooseDefaultSettingsForMessagesLabel')}:
                    </p>
                  </div>
                  <section className="px-6 pt-6 pb-8 space-y-4 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
                    <h2 className="text-base font-semibold flex items-center">
                      {t('CreateCampaignPage.subscriptionPreferenceLabel')}
                    </h2>
                    <p className="text-custom-aliceBlue">{t('CreateCampaignPage.chooseSubscriptionPreference')}:</p>
                    <div className="flex flex-row gap-4">
                      <Chip
                        label={t('CreateCampaignPage.allSubscribedPeopleOption')}
                        variant={allSubscribedPeopleIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setAllSubscribedPeopleIsActive(true);
                          setAllSubscribedAndUnsubscribedPeopleIsActive(false);
                        }}
                      />
                      <Chip
                        label={t('CreateCampaignPage.allSubscribedUnsubscribedPeopleOption')}
                        variant={allSubscribedAndUnsubscribedPeopleIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setAllSubscribedAndUnsubscribedPeopleIsActive(true);
                          setAllSubscribedPeopleIsActive(false);
                        }}
                      />
                    </div>
                  </section>
                  <div className="flex justify-end">
                    <Button variant="cancel" onClick={() => navigate(AppRoutes.campaigns)}>
                      {t('CreateCampaignPage.linkCancel')}
                    </Button>
                    <Button type="button" className="ml-8" onClick={onNext}>
                      {t('CreateCampaignPage.buttonNext')}
                    </Button>
                  </div>
                </div>
              )}
              {/* Goal & Exit step */}
              {activeStep === 3 && (
                <div className="space-y-6 max-w-[50rem]">
                  <div>
                    <h1 className="text-custom-aliceBlue font-semibold text-xl mb-4">
                      {t('CreateCampaignPage.setGoalAndConversionCriteriaLabel')}
                    </h1>
                    <p className="text-custom-aliceBlue mt-4 w-[85%]">
                      {t('CreateCampaignPage.goalsAndConversionsDescription')}:
                    </p>
                  </div>
                  <section className="px-6 pt-6 pb-8 space-y-8 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
                    <div className="flex flex-row gap-4">
                      <Chip
                        label={t('CreateCampaignPage.setGoalOption')}
                        variant={goalIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setGoalIsActive(true);
                          setNoGoalIsActive(false);
                        }}
                      />
                      <Chip
                        label={t('CreateCampaignPage.noGoalOption')}
                        variant={noGoalIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setNoGoalIsActive(true);
                          setGoalIsActive(false);
                        }}
                      />
                    </div>
                    {goalIsActive && (
                      <>
                        <div>
                          <p className="text-custom-aliceBlue font-sem">{t('CreateCampaignPage.customerLabel')}</p>
                          <div className="flex flex-row gap-4 mt-4">
                            <Chip
                              label={t('CreateCampaignPage.entersSegmentOption')}
                              variant={entersSegmentIsActive ? 'dark' : 'gray'}
                              className="hover:cursor-pointer"
                              onClick={() => {
                                setEntersSegmentIsActive(true);
                                setLeavesSegmentIsActive(false);
                              }}
                            />
                            <Chip
                              label={t('CreateCampaignPage.leavesSegmentOption')}
                              variant={leavesSegmentIsActive ? 'dark' : 'gray'}
                              className="hover:cursor-pointer"
                              onClick={() => {
                                setLeavesSegmentIsActive(true);
                                setEntersSegmentIsActive(false);
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <p className="mb-4">
                            <Trans
                              i18nKey="CreateCampaignPage.conversionCountLabel"
                              components={{
                                primary: <span className="font-semibold" />,
                              }}
                            />
                            :
                          </p>
                          <div className="flex flex-row items-baseline gap-x-4">
                            <Input
                              placeholder={t('CreateCampaignPage.durationPlaceholder')}
                              {...register('conversionDuration')}
                              error={errors.conversionDuration?.message as string}
                              className="border-gray-700 w-36"
                              type="number"
                            />
                            <OptionPicker
                              options={timeOptions}
                              selectedOption={selectedTimeOption}
                              setSelectedOption={setSelectedTimeOption}
                            />
                            <p>{t('CreateCampaignPage.ofLabel')}</p>
                            <OptionPicker
                              options={actionOptions}
                              selectedOption={selectedActionOption}
                              setSelectedOption={setSelectedActionOption}
                            />
                            <p>{t('CreateCampaignPage.anyDeliveryFromCampaignLabel')}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </section>
                  <section className="px-6 pt-6 pb-8 space-y-8 shadow rounded-lg bg-custom-gray">
                    <p className="text-custom-aliceBlue font-semibold">
                      {t('CreateCampaignPage.peopleExitCampaignLabel')}:
                    </p>
                    <div className="flex flex-row flex-wrap gap-x-4 gap-y-2">
                      <Chip
                        label={t('CreateCampaignPage.theyAchieveTheGoalOption')}
                        variant={achieveGoalIsAactive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setAchieveTheGoalIsActive(
                            stopMatchingTriggerIsActive === false &&
                              peopleDontExitEarlyIsActive === false &&
                              achieveGoalOrStopMatchingTriggerIsActive === false &&
                              achieveGoalIsAactive === false
                              ? false
                              : true,
                          );
                          setAchieveGoalOrStopMatchingTriggerIsActive(false);
                          setStopMatchingTriggerIsActive(false);
                          setPeopleDontExitEarlyIsActive(false);
                        }}
                      />
                      <Chip
                        label={t('CreateCampaignPage.theyStopMatchingTriggerOption')}
                        variant={stopMatchingTriggerIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setAchieveTheGoalIsActive(false);
                          setAchieveGoalOrStopMatchingTriggerIsActive(false);
                          setStopMatchingTriggerIsActive(
                            achieveGoalIsAactive === false &&
                              peopleDontExitEarlyIsActive === false &&
                              achieveGoalOrStopMatchingTriggerIsActive === false &&
                              stopMatchingTriggerIsActive === false
                              ? false
                              : true,
                          );
                          setPeopleDontExitEarlyIsActive(false);
                        }}
                      />
                      <Chip
                        label={t('CreateCampaignPage.theyAchieveTheGoalOrStopMatchingTrigger')}
                        variant={achieveGoalOrStopMatchingTriggerIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setAchieveTheGoalIsActive(false);
                          setAchieveGoalOrStopMatchingTriggerIsActive(
                            achieveGoalIsAactive === false &&
                              peopleDontExitEarlyIsActive === false &&
                              stopMatchingTriggerIsActive === false &&
                              achieveGoalOrStopMatchingTriggerIsActive === false
                              ? false
                              : true,
                          );
                          setStopMatchingTriggerIsActive(false);
                          setPeopleDontExitEarlyIsActive(false);
                        }}
                      />
                      <Chip
                        label={t('CreateCampaignPage.peopleDontExitEarly')}
                        variant={peopleDontExitEarlyIsActive ? 'dark' : 'gray'}
                        className="hover:cursor-pointer"
                        onClick={() => {
                          setAchieveTheGoalIsActive(false);
                          setAchieveGoalOrStopMatchingTriggerIsActive(false);
                          setStopMatchingTriggerIsActive(false);
                          setPeopleDontExitEarlyIsActive(
                            achieveGoalIsAactive === false &&
                              achieveGoalOrStopMatchingTriggerIsActive === false &&
                              stopMatchingTriggerIsActive === false &&
                              peopleDontExitEarlyIsActive === false
                              ? false
                              : true,
                          );
                        }}
                      />
                    </div>
                  </section>
                  <div className="flex justify-end">
                    <Button variant="cancel" onClick={() => navigate(AppRoutes.campaigns)}>
                      {t('CreateCampaignPage.linkCancel')}
                    </Button>
                    <Button type="button" className="ml-8" onClick={onNext}>
                      {t('CreateCampaignPage.buttonNext')}
                    </Button>
                  </div>
                </div>
              )}
              {/* Workflow step */}
              {activeStep === 4 && (
                <div className="lg:w-[70rem] 3xl:w-[90rem]">
                  <div className="flex flex-row gap-4">
                    <ReactFlowProvider>
                      {/* Redesigned Sidebar */}
                      <section className="h-fit lg:w-[18.5rem] 3xl:w-[23.25rem] p-6 shadow-md rounded-lg bg-gray-800 text-gray-100">
                        {/* Header */}
                        <div className="flex items-center mb-6 pb-4 border-b border-gray-700">
                          <div className="w-6 border-l-2 border-yellow-400 h-6 mr-3"></div>
                          <h1 className="font-medium text-lg">{t('CreateCampaignPage.buildLabel', 'Build')}</h1>
                        </div>

                        {/* Sidebar Sections */}
                        <div className="space-y-7">
                          {/* Messages Section */}
                          <div>
                            <div className="flex items-center mb-3">
                              <div className="w-2 h-2 rounded-sm bg-blue-400 mr-2"></div>
                              <span className="uppercase text-xs font-medium tracking-wider text-gray-400">
                                {t('CreateCampaignPage.messagesLabel', 'Messages')}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {messageNodes.map((item, id) => {
                                // Determine which icon to use based on the item
                                let icon = null;
                                let borderColor = 'border-indigo-500';
                                if (item.includes('Email')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-indigo-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Push')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-purple-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                      />
                                    </svg>
                                  );
                                  borderColor = 'border-purple-500';
                                } else if (item.includes('SMS')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-blue-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                      />
                                    </svg>
                                  );
                                  borderColor = 'border-blue-500';
                                }

                                return (
                                  <div
                                    key={id}
                                    className="dndnode group relative"
                                    draggable
                                    onDragStart={(event) => onDragStart(event, item, 'bg-secondary-400', '☎️')}
                                  >
                                    <div
                                      className={`bg-gray-700 border-l-4 ${borderColor} hover:bg-gray-600 text-white px-4 py-2 rounded-sm flex items-center transition-all duration-200 cursor-pointer`}
                                    >
                                      {icon}
                                      <span>{item}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Data Section */}
                          <div>
                            <div className="flex items-center mb-3">
                              <div className="w-2 h-2 rounded-sm bg-yellow-400 mr-2"></div>
                              <span className="uppercase text-xs font-medium tracking-wider text-gray-400">
                                {t('CreateCampaignPage.dataLabel', 'Data')}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              {dataNodes.map((item, id) => {
                                // Determine icons based on the node type
                                let icon = null;

                                if (item.includes('Batch Update')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-amber-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Send Event')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-amber-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Manual Segment')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-amber-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Query Collection')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-amber-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Create or Update Person')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-amber-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                                      />
                                    </svg>
                                  );
                                }

                                return (
                                  <div
                                    key={id}
                                    className="dndnode group relative"
                                    draggable
                                    onDragStart={(event) => onDragStart(event, item, 'bg-yellow-200', '🧱')}
                                  >
                                    <div className="bg-gray-700 border-l-4 border-amber-500 hover:bg-gray-600 text-white px-4 py-2 rounded-sm flex items-center transition-all duration-200 cursor-pointer">
                                      {icon}
                                      <span>{item}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Delays Section */}
                          <div>
                            <div className="flex items-center mb-3">
                              <div className="w-2 h-2 rounded-sm bg-red-400 mr-2"></div>
                              <span className="uppercase text-xs font-medium tracking-wider text-gray-400">
                                {t('CreateCampaignPage.delaysLabel', 'Delays')}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {delaysNodes.map((item, id) => {
                                // Determine the correct icon based on the delay type
                                let icon = null;

                                if (item.includes('Wait Until')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-red-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Time Delay')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-red-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Time Window')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-red-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                  );
                                }

                                return (
                                  <div
                                    key={id}
                                    className="dndnode group relative"
                                    draggable
                                    onDragStart={(event) => onDragStart(event, item, 'bg-red-400', '🕗')}
                                  >
                                    <div className="bg-gray-800 border-l-4 border-red-500 hover:bg-gray-700 text-white px-4 py-2 rounded-sm flex items-center transition-all duration-200 cursor-pointer">
                                      {icon}
                                      <span>{item}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Flow Control Section */}
                          <div>
                            <div className="flex items-center mb-3">
                              <div className="w-2 h-2 rounded-sm bg-green-400 mr-2"></div>
                              <span className="uppercase text-xs font-medium tracking-wider text-gray-400">
                                {t('CreateCampaignPage.flowControlLabel', 'Flow Control')}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              {flowControlNodes.map((item, id) => {
                                // Determine the correct icon based on the flow control type
                                let icon = null;

                                if (item.includes('True/False')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-green-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Multi-Split')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-green-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                  );
                                } else if (item.includes('Exit')) {
                                  icon = (
                                    <svg
                                      className="w-4 h-4 mr-2 text-green-400"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                      />
                                    </svg>
                                  );
                                }

                                return (
                                  <div
                                    key={id}
                                    className="dndnode group relative"
                                    draggable
                                    onDragStart={(event) => onDragStart(event, item, 'bg-primary-400', '👀')}
                                  >
                                    <div className="bg-gray-800 border-l-4 border-green-500 hover:bg-gray-700 text-white px-4 py-2 rounded-sm flex items-center transition-all duration-200 cursor-pointer">
                                      {icon}
                                      <span>{item}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Diagram Area - Unchanged */}
                      <div className="space-y-6 lg:w-[50rem] 3xl:w-[66.75rem]">
                        <Diagram />
                        <div className="flex justify-end">
                          <Button variant="cancel" onClick={() => navigate(AppRoutes.campaigns)}>
                            {t('CreateCampaignPage.linkCancel')}
                          </Button>
                          <Button type="button" className="ml-8" onClick={onNext}>
                            {t('CreateCampaignPage.buttonNext')}
                          </Button>
                        </div>
                      </div>
                    </ReactFlowProvider>
                  </div>
                </div>
              )}
              {/* Review step */}
              {activeStep === 5 && (
                <CreateCampaignLastStep
                  selectedTriggerTitle={selectedTrigger.triggerTitle}
                  allSubscribedPeopleIsActive={allSubscribedPeopleIsActive}
                  goalIsActive={goalIsActive}
                  entersSegmentIsActive={entersSegmentIsActive}
                  selectedTimeOptionText={selectedTimeOption.text}
                  selectedActionOptionText={selectedActionOption.text}
                  exitCondition={exitCondition}
                  segmentData={segmentData}
                  setActiveStep={setActiveStep}
                  setToastInfo={setToastInfo}
                  setOpen={setOpen}
                />
              )}
            </form>
          </FormProvider>
        </div>
        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={toastInfo.toastType as ToastType}
          />
        </ProvideToasts>
      </div>
    </>
  );
};

export default CreateCampaign;
